import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import styles from './SchedulingWidget.module.css';

const CALENDLY_SCRIPT_ID = 'calendly-widget-bootstrap';
const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';

interface CalendlyEmbedProps extends Omit<ComponentProps<'div'>, 'children' | 'ref'> {
  url: string;
  fallback?: ReactNode;
}

export default function CalendlyEmbed({ url, fallback, className, ...props }: CalendlyEmbedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const parent = ref.current;
    if (!parent) {
      return;
    }

    const handleMessage = (e: MessageEvent) => {
      const isCalendlyEvent = e.origin === 'https://calendly.com' && e.data.event?.startsWith('calendly.');

      if (isCalendlyEvent && e.data.event === 'calendly.event_type_viewed') {
        setLoading(false);
      }
    };

    const init = () => {
      if (!window.Calendly || !ref.current) {
        return;
      }
      ref.current.replaceChildren();
      const urlWithParams = new URL(url);
      urlWithParams.searchParams.append('hide_gdpr_banner', '1');
      urlWithParams.searchParams.append('hide_landing_page_details', '1');

      window.Calendly.initInlineWidget({ url: urlWithParams.toString(), parentElement: ref.current, resize: true });
    };

    const handleError = () => setFailed(true);

    let script = document.getElementById(CALENDLY_SCRIPT_ID) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = CALENDLY_SCRIPT_ID;
      script.async = true;
      script.src = CALENDLY_SCRIPT_SRC;
      document.head.appendChild(script);
    }

    if (window.Calendly) {
      init();
    } else {
      script.addEventListener('load', init);
      script.addEventListener('error', handleError);
      window.addEventListener('message', handleMessage);
    }

    return () => {
      script?.removeEventListener('load', init);
      script?.removeEventListener('error', handleError);
      window.removeEventListener('message', handleMessage);
      parent.replaceChildren();
    };
  }, [url]);

  if (failed) {
    return (
      <div className={clsx(styles.fallback, styles.calendly, className)}>
        {fallback ?? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {url}
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      {...props}
      ref={ref}
      className={clsx(styles.widget, styles.calendly, loading && styles.loading, className)}
    />
  );
}
